import Model from './Model'
import User from "./User";
import Project from "./Project";

const defaultPermissions = {
    can_view_brief: true,
    can_create_project: true,
    can_add_remove_positions: true,
    can_edit_project_and_positions: true,
    can_view_talent: true,
    can_invite_talent: true,
    can_request_interview: true,
    can_assign_talent: true,
    can_view_sow: true,
    can_request_sow: true,
    can_edit_sow: true,
    can_complete_sow: true,
}

export default class ProjectGroup extends Model {
    static Key = 'ProjectGroup';

    static ADMIN_PERMISSIONS = defaultPermissions;

    static READ_ONLY_PERMISSIONS = {
        can_view_brief: true,
        can_create_project: false,
        can_add_remove_positions: false,
        can_edit_project_and_positions: false,
        can_view_talent: true,
        can_invite_talent: false,
        can_request_interview: false,
        can_assign_talent: false,
        can_view_sow: true,
        can_request_sow: false,
        can_edit_sow: false,
        can_complete_sow: false,
    }

    static GroupedPermissions = {
        'Project Brief': {
            'Can View': 'can_view_brief',
            'Create Project': 'can_create_project',
            'Add & Remove Roles': 'can_add_remove_positions',
            'Update Project & Role Requirements': 'can_edit_project_and_positions',
        },
        'Talent Selection': {
            'Can View': 'can_view_talent',
            'Invite To Project': 'can_invite_talent',
            // 'Request Interview': 'can_request_interview',
            // 'Select For Position': 'can_assign_talent',
            'Interview / Message Talent': 'can_request_interview',
            'Select For Role': 'can_assign_talent',
        },
        'SoW': {
            'Can View': 'can_view_sow',
            'Request SoW': 'can_request_sow',
            'Edit': 'can_edit_sow',
            'Final Signature': 'can_complete_sow',
        }
    }

    static normalize(props) {
        props.members = TB.map(User.makeMany(props.members || []), member => {
            // Set default permissions if missing
            if (member.isClientSuperAdmin()) {
                member.permissions = this.ADMIN_PERMISSIONS;
            }else if (TB.auth().user()?.canManageGroups() && !member.permissions) {
                member.permissions = this.READ_ONLY_PERMISSIONS;
            } else {
                // Grab only the permission keys needed
                member.permissions = TB.pick(member.permissions, Object.keys(defaultPermissions));
            }
            return member
        });

        // sort by is super admin, then by admin , then by first name
        props.members.sort((a, b) => {
            return b.isClientSuperAdmin() - a.isClientSuperAdmin() || b.isClientAdmin() - a.isClientAdmin() || a.first_name.localeCompare(b.first_name)
        })

        return props;
    }

    displayCurrentMembers() {
        let names = '';

        for (let member of this.members) {
            names += member.firstNamePlusInitial().slice(0, -1);

            if (!TB.isEqual(TB.last(this.members), member)) {
                names += ', ';
            }
        }

        return names;
    }

    separateAdminsAndMembers() {
        return TB.partition(this.members, (member) => {
            return member.isClientAdmin();
        });
    }

    static CreateGroupRequest;
    static UpdateGroupRequest;
    static FetchGroupRequest;
    static DeleteGroupRequest;
    static ShareRequest;

    static async fetchAllForClient(client_id, requestOptions = {}) {
        const user = TB.auth().user();

        if (!user || !user.hasRegisteredClientOrg()) {
            throw new Error('Unauthorized')
        }

        this.FetchGroupRequest = TB.request(TB.api_endpoint(`/api/clients/${client_id}/groups`));

        const response = await this.FetchGroupRequest.get(requestOptions).catch(console.warn);

        if (!TB.has(response, 'success.data.client_project_groups')) {
            throw new Error("Something went wrong")
        }
        let groups = ProjectGroup.makeMany(response.success.data.client_project_groups);
        let projects = response.success.data.projects;

        let publicGroup = {
            name: 'Public',
            projects_count: response.success.data.client_project_public_count
        }

        if (projects) {
            projects = TB.orderBy(Project.makeMany(projects), ['created_at','asc']);
            let [groupProjects, publicProjects] = TB.partition(projects, p => TB.isDefined(p?.group?.id));

            publicGroup.projects = publicProjects;

            let projectMapping = TB.groupBy(groupProjects, 'group.id');

            groups = TB.map(groups, group => {
                group.projects = TB.orderBy(projectMapping[group.id] || [], 'created_at');
                return group;
            })
        }

        return {
            groups,
            publicGroup,
            projects
        }
    }

    static async create(props, requestOptions = {}) {

        let group = ProjectGroup.make({...props})
        let auth = TB.auth().user();

        if (!auth || !auth.canManageGroups() || !group?.members?.length || !group?.name) {
            throw new Error('Unauthorized')
        }

        if (group.projects?.length) {
            group.projects = TB.map(group.projects, 'id');
        }

        this.CreateGroupRequest = TB.request(
            `/api/clients/${auth.clientOrg().id}/groups/create`,
            group
        );

        let response = await this.CreateGroupRequest.post(requestOptions);

        if (TB.has(response, 'success.data.auth')) {
            await TB.auth().update({...response.success.data.auth})
        }

        if (!TB.has(response, 'success.data.client_project_group')) {
            throw new Error('Something went wrong')
        }

        let updatedGroup = this.make(response.success.data.client_project_group);

        // await updateProjectGroupQuery(auth.clientOrg().id,updatedGroup);

        return updatedGroup;
    }


    async update(props = {}, requestOptions = {}) {

        let group = ProjectGroup.make({...this, ...props})

        let auth = TB.auth().user();

        if (!auth || !(auth.canManageGroups() || auth.isClientAdmin()) || !group?.members?.length || !group?.name) {
            throw new Error('Unauthorized')
        }

        if (group.projects?.length) {
            group.projects = TB.map(group.projects, 'id');
        }

        ProjectGroup.UpdateGroupRequest = TB.request(
            `/api/clients/${auth.clientOrg().id}/groups/${this.id}/update`,
            group
        );

        let response = await ProjectGroup.UpdateGroupRequest.post(requestOptions);

        if (TB.has(response, 'success.data.auth')) {
            await TB.auth().update({...response.success.data.auth})
        }

        if (!TB.has(response, 'success.data.client_project_group')) {
            throw new Error('Something went wrong')
        }

        let updatedGroup = ProjectGroup.make(response.success.data.client_project_group);

        // await updateProjectGroupQuery(auth.clientOrg().id,updatedGroup);

        return updatedGroup;
    }

    async delete(props = {}) {

        let auth = TB.auth().user();

        if (!auth || !auth.canManageGroups()) {
            return false
        }

        ProjectGroup.DeleteGroupRequest = TB.request(
            `/api/clients/${auth.clientOrg().id}/groups/${this.id}/delete`, props
        );

        let response = await ProjectGroup.DeleteGroupRequest.post();

        if (!TB.has(response, 'success')) {
            return false;
        }

        if (TB.has(response, 'success.data.auth')) {
            await TB.auth().update({...response.success.data.auth})
        }

        if (TB.has(response, 'success.data.client_project_group')) {
            return ProjectGroup.make(response.success.data.client_project_group);
        }

        return true
    }

    static cached() {
        return this.Cached ? this.Cached : [];
    }

    static async submitShare(project, position, formModel) {
        let auth = TB.auth().user();
        if (!auth || !(auth.canManageGroups() || auth.isClientAdmin())) {
            return false
        }

        this.ShareRequest = TB.request(
            TB.api_endpoint(`/api/clients/${auth.clientOrg().id}/projects/${project.id}/positions/${position.id}/candidates/share`), formModel
        );
        console.log(this.ShareRequest, 'this.share request')
        let response = await this.ShareRequest.post().catch(console.warn);

        console.log(response, 'responseeeeee from share modal submit')

        // need to return correct info
        // returning response.success will close modal (for now)

        return response;
    }
}

const updateProjectGroupQuery = (client_id, updatedGroup) => {
    return TB.queryClient.setQueriesData(['fetchClientProjectGroups', client_id], (data) => {
        if (!data) return undefined;

        let update = {...(data || {})};

        let updatedGroupIndex = TB.findIndex(update.groups, ['id', updatedGroup.id]);

        if(!updatedGroupIndex) {
            update.groups = [
                ...update.groups,
                ProjectGroup.make(updatedGroup)
            ]
        }else {
            update = TB.set(update,`groups[${updatedGroupIndex}]`, ProjectGroup.make(updatedGroup))
        }

        return update;
    });
}

const refreshClientProjectGroups = (client_id) => {
    return TB.queryClient.invalidateQueries(['fetchClientProjectGroups', client_id]);
}


export {
    updateProjectGroupQuery,
    refreshClientProjectGroups,
}